import { Popover, RadioGroup, Transition } from '@headlessui/react';
import { SelectorIcon } from '@heroicons/react/outline';
import { CloseIcon, FilterIcon } from 'components/icons';
import { Tooltip } from 'components/ui';
import { omit, orderBy } from 'lodash';
import querystring from 'querystring';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store';
import {
  clearApplied,
  clearSelected,
  composableFiltersState,
  selectSegment,
} from 'store/composableFiltersSlice';
import { useGetCustomerSegmentsQuery } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { SegmentInfo } from '../../store/services/types';

export const SegmentSelector = ({ embedded }: { embedded?: boolean }) => {
  const { rawQueryParams } = useParseURLQueryParams();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { data, applied, selected } = useAppSelector(composableFiltersState);

  const { isFetching: isFetchingSegments, data: segments } =
    useGetCustomerSegmentsQuery(
      { serializedStoreName: `store_name=${rawQueryParams?.store_name}` },
      {
        skip: !rawQueryParams?.store_name,
      }
    );

  const segment = useMemo(() => {
    if (embedded) {
      return selected.segment;
    }

    return applied.segment;
  }, [applied.segment, embedded, selected.segment]);

  useEffect(() => {
    if (!segments || !rawQueryParams.segment_name || embedded) return;

    const segment =
      data?.segments?.find(
        (segment) =>
          segment.segment_name.toLowerCase() ===
          rawQueryParams.segment_name?.toLowerCase()
      ) ?? undefined;

    if (segment) {
      dispatch(
        selectSegment({
          segmentName: segment.segment_name,
          directApply: true,
        })
      );
    } else if (!segment) {
      if (rawQueryParams?.segment_name) {
        history.push({
          search: querystring.stringify(
            omit({ ...rawQueryParams }, ['segment_name'])
          ),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments]);

  const handleSelectSegment = (selectedSegment: any) => {
    if (!selectedSegment) {
      dispatch(embedded ? clearSelected() : clearApplied());

      if (rawQueryParams?.segment_name) {
        history.push({
          search: querystring.stringify(
            omit({ ...rawQueryParams }, ['segment_name'])
          ),
        });
      }

      return;
    }

    dispatch(
      selectSegment({
        segmentName: selectedSegment,
        directApply: !embedded,
      })
    );

    history.push({
      search: querystring.stringify({
        ...rawQueryParams,
        segment_name: selectedSegment,
      }),
    });
  };

  const segmentOptions = useMemo(() => {
    const prebuilt: string[] = [
      'At risk new signups',
      'At risk one timers',
      'At risk repeats',
      'At risk loyals',
      'Active new signups',
      'Active one timers',
      'Active repeats',
      'Active loyals',
      'Churned signups',
      'Churned one timers',
      'Churned repeats',
      'Churned loyals',
      'Most likely to buy',
    ];
    const favorite: string[] = [];
    const custom: string[] = [];
    const premium: string[] = [];

    if (data) {
      const orderedData = orderBy(segments, ['segment_name'], ['asc']);

      for (const segment of orderedData) {
        if (!prebuilt.includes(segment.segment_name)) {
          if (
            segment.json_filter?.filter?.filters?.find((filter) =>
              filter.name?.startsWith('managed_segment')
            )
          ) {
            premium.push(segment.segment_name);
          } else {
            custom.push(segment.segment_name);
          }
        }

        if (segment.favorite) {
          favorite.push(segment.segment_name);
        }
      }
    }
    return {
      premium,
      favorite,
      prebuilt,
      custom,
    };
  }, [data, segments]);

  const [segmentCategory, setSegmentCategory] = useState<
    'favorite' | 'custom' | 'prebuilt' | 'premium'
  >('favorite');

  const [filter, setFilter] = useState('');

  const filteredOptions = useMemo(() => {
    const options = segmentOptions[segmentCategory];
    if (filter) {
      return options.filter((o) =>
        o.toLowerCase().includes(filter.toLowerCase())
      );
    }

    return options;
  }, [filter, segmentCategory, segmentOptions]);

  useEffect(() => {
    if (!segment) {
      return;
    }

    if (segment.favorite) {
      setSegmentCategory('favorite');
      return;
    }

    if (segmentOptions.prebuilt.includes(segment.segment_name)) {
      setSegmentCategory('prebuilt');
    } else {
      setSegmentCategory('custom');
    }
  }, [segment, segmentOptions.prebuilt]);

  const clearSelection = (
    e:
      | React.MouseEvent<HTMLSpanElement, MouseEvent>
      | React.KeyboardEvent<HTMLSpanElement>,
    close: () => void
  ) => {
    e.stopPropagation();
    handleSelectSegment(undefined);
    close();
  };

  const hasSegmentsInTheCategory = filteredOptions.length > 0;

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            disabled={isFetchingSegments}
            className="focus-visible:ring-offset-dark-primary-base text-gray-800 relative flex w-full cursor-pointer items-center  space-x-2 rounded-lg py-2 pl-3 pr-8 text-left text-base font-semibold tracking-[0.015em] focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            <FilterIcon className="w-5 h-5" />
            <span className="block truncate font-semibold">
              {isFetchingSegments
                ? ' Loading segments...'
                : segment?.segment_name ?? 'All users'}
            </span>
            {segment?.segment_name && (
              <Tooltip text="Clear selection" placement="bottom">
                <span
                  role="link"
                  aria-label="clear selection"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    clearSelection(e, close);
                  }}
                  onClick={(e) => {
                    clearSelection(e, close);
                  }}
                >
                  <CloseIcon className="h-4 w-4" />
                </span>
              </Tooltip>
            )}
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <SelectorIcon aria-hidden="true" className="w-5 h-5" />
            </span>
          </Popover.Button>
          <Transition
            as={Fragment}
            show={open}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              static
              className="bg-white absolute left-0 z-10 mt-2 flex h-96 w-screen max-w-lg  flex-col justify-between shadow-2xl ring-1 ring-black ring-opacity-10"
            >
              <div className="grid h-full grid-cols-3 overflow-y-hidden">
                <div className="border-r-gray-500 h-full border-r">
                  <RadioGroup
                    value={segmentCategory}
                    onChange={setSegmentCategory}
                    className="mt-4"
                  >
                    {Object.keys(segmentOptions).map((category) => (
                      <RadioGroup.Option value={category} key={category}>
                        {({ checked }) => (
                          <span
                            className={` text-light-text-main dark:text-dark-text-main hover:bg-segments-accent-green-3  block cursor-pointer py-2 pl-8 pr-4 first-letter:capitalize
                             ${checked ? 'bg-segments-accent-green-3' : ''}`}
                          >
                            {category}
                          </span>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </RadioGroup>
                </div>
                <div className="col-span-2 overflow-y-hidden ">
                  {hasSegmentsInTheCategory ? (
                    <SelectSegment
                      segment={segment}
                      handleSelectSegment={handleSelectSegment}
                      close={close}
                      filter={filter}
                      setFilter={setFilter}
                      filteredOptions={filteredOptions}
                    />
                  ) : (
                    <NoSegmentsPlaceholder />
                  )}
                  <div />
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

function NoSegmentsPlaceholder() {
  // const { rawQueryParams } = useParseURLQueryParams();
  // const history = useHistory();

  // function handleRedirectToChangePlan() {
  //   history.push({
  //     pathname: '/account/change-plan',
  //     search: querystring.stringify({
  //       store_name: rawQueryParams?.store_name,
  //     }),
  //   });
  // }

  return (
    <div className="h-full flex justify-center items-center px-8">
      <div>
        <p className=" text-base text-center">
          No segments available for this category.
        </p>
      </div>
    </div>
  );
}

function SelectSegment({
  filter,
  setFilter,
  filteredOptions,
  segment,
  handleSelectSegment,
  close,
}: {
  filter: string;
  setFilter: (filter: string) => void;
  filteredOptions: string[];
  segment: SegmentInfo | undefined;
  handleSelectSegment: (segment: any) => void;
  close: () => void;
}) {
  return (
    <>
      <div className="p-4">
        <input
          aria-label="search"
          type="search"
          placeholder="search..."
          className="bg-light-main  focus:ring-dark-primary-base focus:border-dark-primary-base text-light-text-main dark:text-dark-text-main focus:dark:text-light-text-main w-full rounded-md border border-[#BFBFBF] bg-transparent text-sm focus:bg-white focus:ring-1 dark:border-[#8F8F8F] dark:bg-transparent focus:dark:bg-white"
          value={filter}
          spellCheck={false}
          onChange={(e) => {
            setFilter(e.target.value);
          }}
        />
      </div>

      <div className=" h-[78%] overflow-y-auto">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((item) => (
            <button
              key={item}
              type="button"
              className={`text-light-text-main dark:text-dark-text-main hover:bg-segments-accent-green-3  block w-full cursor-pointer py-2 px-6 text-left first-letter:capitalize ${
                item.toLowerCase() === segment?.segment_name?.toLowerCase() &&
                ' bg-segments-accent-green-3'
              }`}
              onClick={() => {
                handleSelectSegment(item);
                close();
              }}
            >
              {item}
            </button>
          ))
        ) : (
          <div className="grid place-items-center h-full text-black/50">
            <p>No segments found</p>
          </div>
        )}
      </div>
    </>
  );
}
//
// function UpgradeGraphics() {
//   return (
//     <svg
//       width="220"
//       height="150"
//       fill="none"
//       xmlns="http://www.w3.org/2000/svg"
//     >
//       <path fill="#fff" d="M0 0h220v150H0z" />
//       <rect
//         opacity=".5"
//         x="7"
//         y="27"
//         width="207"
//         height="36"
//         rx="10"
//         fill="#F0F0F0"
//       />
//       <path
//         fillRule="evenodd"
//         clipRule="evenodd"
//         d="M24 45a4 4 0 0 1 4-4h75a4 4 0 0 1 0 8H28a4 4 0 0 1-4-4Z"
//         fill="#EBEBEB"
//       />
//       <circle cx="117" cy="45" r="4" fill="#7DA9B3" />
//       <rect
//         opacity=".5"
//         x="7"
//         y="69"
//         width="146"
//         height="28"
//         rx="7"
//         fill="#F0F0F0"
//       />
//       <path
//         fillRule="evenodd"
//         clipRule="evenodd"
//         d="M18.99 83c0-1.718 1.264-3.111 2.822-3.111H74.71c1.558 0 2.821 1.393 2.821 3.111 0 1.718-1.263 3.111-2.82 3.111h-52.9c-1.558 0-2.82-1.393-2.82-3.111Z"
//         fill="#EBEBEB"
//       />
//       <ellipse cx="84.585" cy="83" rx="2.821" ry="3.111" fill="#7DA9B3" />
//       <rect
//         opacity=".5"
//         x="7"
//         y="103"
//         width="113"
//         height="19.652"
//         rx="5"
//         fill="#F0F0F0"
//       />
//       <path
//         fillRule="evenodd"
//         clipRule="evenodd"
//         d="M16.28 112.826c0-1.206.978-2.183 2.184-2.183h40.942a2.183 2.183 0 1 1 0 4.367H18.464a2.184 2.184 0 0 1-2.184-2.184Z"
//         fill="#EBEBEB"
//       />
//       <circle cx="67.048" cy="112.826" r="2.184" fill="#7DA9B3" />
//       <rect x="160" y="69" width="54" height="54" rx="27" fill="#EAF6F4" />
//       <path
//         d="m200.906 96.406-13.484-13.484m0 0-13.5 13.484m13.5-13.484v27"
//         stroke="#125970"
//         strokeWidth="3"
//       />
//     </svg>
//   );
// }
